/**
 * Bridge ESM ↔ legacy: API pública de lectura.
 *
 * Los controllers Stimulus / Vue / libs importan desde aquí,
 * no usan variables globales implícitas (comentarios eslint global).
 *
 * Escritura en `window`: solo vía `@/legacy/registry`.
 *
 * `Cifra` / `JST` son proxies vivos hacia `window.*`: el concat legacy puede
 * reasignar `window.Cifra` (p.ej. `var Cifra = Cifra || {}`), y un `export const`
 * fijado al objeto inicial dejaría a Stimulus sin `Modals` / `App` / etc.
 */

import {
  ensureCifraAppNamespace as ensureCifraAppNamespaceRegistry,
  registerGlobalsSkeleton,
  syncGlobalsFromWindow,
} from '@/legacy/registry';

registerGlobalsSkeleton();

function createLiveObjectGlobal(getter) {
  return new Proxy(
    {},
    {
      get(_target, prop) {
        const obj = getter();
        if (obj == null) return undefined;
        const value = obj[prop];
        if (typeof value !== 'function') return value;
        return value.bind(obj);
      },
      set(_target, prop, value) {
        const obj = getter();
        if (obj == null) return false;
        obj[prop] = value;
        return true;
      },
      has(_target, prop) {
        const obj = getter();
        return obj != null && prop in obj;
      },
      ownKeys() {
        return Reflect.ownKeys(getter() || {});
      },
      getOwnPropertyDescriptor(_target, prop) {
        const obj = getter();
        if (obj == null || !(prop in obj)) return undefined;
        return {
          configurable: true,
          enumerable: true,
          writable: true,
          value: obj[prop],
        };
      },
    },
  );
}

function createLiveGlobal(getter) {
  return new Proxy(function legacyLiveGlobal() {}, {
    construct(_target, args) {
      const obj = getter();
      if (typeof obj !== 'function') {
        throw new TypeError('[cifra] El global legacy no es un constructor');
      }
      return Reflect.construct(obj, args, obj);
    },
    apply(_target, thisArg, args) {
      const obj = getter();
      return Reflect.apply(
        obj,
        thisArg === undefined || thisArg === globalThis ? obj : thisArg,
        args,
      );
    },
    get(_target, prop) {
      if (prop === Symbol.toStringTag) return 'Function';
      const obj = getter();
      if (obj == null) return undefined;
      const value = obj[prop];
      if (typeof value !== 'function') return value;
      // No hacer bind de constructores Backbone/Marionette (View.extend, Model.extend, …).
      // bind() crea una función sin las propiedades estáticas.
      if (typeof value.extend === 'function') return value;
      if (
        value.prototype &&
        value.prototype.constructor === value &&
        Object.getOwnPropertyNames(value.prototype).length > 1
      ) {
        return value;
      }
      return value.bind(obj);
    },
    set(_target, prop, value) {
      const obj = getter();
      if (obj == null) return false;
      obj[prop] = value;
      return true;
    },
    has(_target, prop) {
      const obj = getter();
      return obj != null && prop in obj;
    },
  });
}

export const Cifra = createLiveObjectGlobal(() => window.Cifra);
export const JST = createLiveObjectGlobal(() => window.JST);

export const $ = createLiveGlobal(() => window.jQuery || window.$);
export const jQuery = $;
export const _ = createLiveGlobal(() => window._);
export const Backbone = createLiveGlobal(() => window.Backbone);
export const Mn = createLiveGlobal(() => window.Mn || window.Marionette);
export const Marionette = createLiveGlobal(() => window.Marionette || window.Mn);
/** @deprecated Usar `@/libs/i18n` (`t`, `getI18n`, `I18n`). Bridge legacy vía window.I18n. */
export const I18n = createLiveGlobal(() => window.I18n);
export const toastr = createLiveGlobal(() => window.toastr);
export const moment = createLiveGlobal(() => window.moment);
export const swal = createLiveGlobal(() => window.swal);
export const Chart = createLiveGlobal(() => window.Chart);
export const ChartDataLabels = createLiveGlobal(() => window.ChartDataLabels);
export const ChartRenderer = createLiveObjectGlobal(() => window.ChartRenderer);

export function ensureLegacyGlobals() {
  syncGlobalsFromWindow();
}

export function ensureCifraAppNamespace() {
  ensureCifraAppNamespaceRegistry();
}

export function getLegacyGlobals() {
  ensureLegacyGlobals();
  return {
    $,
    jQuery,
    _,
    Backbone,
    Mn,
    Marionette,
    Cifra,
    I18n,
    toastr,
    moment,
    JST,
    swal,
    Chart,
    ChartDataLabels,
    ChartRenderer,
  };
}

function callWindowFn(name, args) {
  const fn = window[name];
  if (typeof fn !== 'function') {
    throw new Error(`[cifra] window.${name} no está definido (¿cargó el legacy?)`);
  }
  return fn(...args);
}

export function set_overlay(...args) {
  return callWindowFn('set_overlay', args);
}

export function set_overlay_ajax(...args) {
  return callWindowFn('set_overlay_ajax', args);
}

export function init_datepicker(...args) {
  return callWindowFn('init_datepicker', args);
}

export function blockUIForDownload(...args) {
  return callWindowFn('blockUIForDownload', args);
}

export function cloneOnScroll(...args) {
  return callWindowFn('cloneOnScroll', args);
}

export function init_payment_percentage_slider(...args) {
  return callWindowFn('init_payment_percentage_slider', args);
}

/** Helper legacy del cuaderno de evaluaciones (classic scope). */
export function ancho_competencias(...args) {
  return callWindowFn('ancho_competencias', args);
}

export function readCifra() {
  return window.Cifra;
}

export function readMn() {
  return window.Mn || window.Marionette;
}

/** @deprecated Usar `getI18n()` desde `@/libs/i18n`. */
export function readI18n() {
  return window.I18n;
}

export function readToastr() {
  return window.toastr;
}

export function readMoment() {
  return window.moment;
}

export function readJST() {
  return window.JST;
}

export function readBackbone() {
  return window.Backbone;
}

export function readLodash() {
  return window._;
}

export function readJQuery() {
  return window.jQuery || window.$;
}
